import React from "react";
import { Box, Typography, Stack, Button } from "@mui/material";
import { motion } from "framer-motion";
import { FileX2, RefreshCw, Database, BarChart3, TrendingUp, Phone } from "lucide-react";

const ICONS = {
    default: FileX2,
    data: Database,
    chart: BarChart3,
    trend: TrendingUp,
    call: Phone,
};

const EmptyState = ({
    title = "No Data Available",
    subtitle = "There's no data to display at the moment. Try adjusting your filters or check back later.",
    icon = "default",
    onRefresh,
    showRefresh = true,
}) => {
    const Icon = ICONS[icon] || FileX2;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                    px: 4,
                    textAlign: "center",
                }}
            >
                {/* Animated Icon Container */}
                <motion.div
                    animate={{
                        y: [0, -8, 0],
                    }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                >
                    <Box
                        sx={{
                            width: 120,
                            height: 120,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 3,
                            position: "relative",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                width: 140,
                                height: 140,
                                borderRadius: "50%",
                                border: "2px dashed #C7D2FE",
                                animation: "spin 20s linear infinite",
                                "@keyframes spin": {
                                    "0%": { transform: "rotate(0deg)" },
                                    "100%": { transform: "rotate(360deg)" },
                                },
                            },
                        }}
                    >
                        <Icon size={48} color="#6366F1" strokeWidth={1.5} />
                    </Box>
                </motion.div>

                {/* Content */}
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: "#1E293B",
                        mb: 1,
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    variant="body2"
                    sx={{
                        color: "#64748B",
                        maxWidth: 360,
                        lineHeight: 1.6,
                        mb: 3,
                    }}
                >
                    {subtitle}
                </Typography>

                {/* Decorative Dots */}
                <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                    {[0, 1, 2].map((i) => (
                        <motion.div
                            key={i}
                            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{
                                duration: 1.5,
                                delay: i * 0.2,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: "#C7D2FE",
                                }}
                            />
                        </motion.div>
                    ))}
                </Stack>

                {/* Refresh Button */}
                {showRefresh && onRefresh && (
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshCw size={16} />}
                            onClick={onRefresh}
                            sx={{
                                borderColor: "#E2E8F0",
                                color: "#64748B",
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: 2,
                                px: 3,
                                "&:hover": {
                                    borderColor: "#6366F1",
                                    color: "#6366F1",
                                    bgcolor: "#EEF2FF",
                                },
                            }}
                        >
                            Refresh Data
                        </Button>
                    </motion.div>
                )}
            </Box>
        </motion.div>
    );
};

export default EmptyState;
